import type { RichTextDoc } from '@bcecloud/portal-ui';
import { route, SandboxError, type Route } from '../adapter';
import { newId, now, type SandboxStore } from '../store';
import type { SandboxResource } from '../seed/portal';
import {
  RESOURCE_ACTION,
  RESOURCE_AUDIENCE,
  RESOURCE_FORMAT,
  RESOURCE_STATUS,
  isHttpsUrl,
  type OrganizationResource,
  type Resource,
  type ResourceAction,
  type ResourceInput,
  type ResourceTopic,
  type ResourceTopicIcon,
} from '@/lib/resources';

/**
 * The Resource center: resources, topics and per-client sharing. The
 * publish rule and the format line follow the API's `resources.domain.ts`.
 */

const WORDS_PER_MINUTE = 200;

/** Every word of an article, for its reading time and the publish check. */
function articleText(doc: RichTextDoc | null | undefined): string {
  const words: string[] = [];
  const walk = (node: unknown): void => {
    if (typeof node !== 'object' || node === null) return;
    const record = node as { text?: unknown; content?: unknown };
    if (typeof record.text === 'string') words.push(record.text);
    if (Array.isArray(record.content)) record.content.forEach(walk);
  };
  walk(doc);
  return words.join(' ');
}

/** What stops a resource publishing, in the API's words. */
export function publishProblems(resource: Pick<SandboxResource, 'title' | 'summary' | 'topic' | 'format' | 'body' | 'file' | 'link'>): string[] {
  const problems: string[] = [];
  if (resource.title.trim() === '') problems.push('Add a title.');
  if (resource.summary.trim() === '') problems.push('Add a summary.');
  if (resource.topic === null) problems.push('Choose a topic.');
  if (resource.format === RESOURCE_FORMAT.article && articleText(resource.body).trim() === '') problems.push('Write the article.');
  if (resource.format === RESOURCE_FORMAT.file && resource.file === null) problems.push('Upload the file.');
  if (resource.format === RESOURCE_FORMAT.link && (resource.link === null || !isHttpsUrl(resource.link.url))) problems.push('Add an https:// link.');
  return problems;
}

function slugify(title: string, taken: ReadonlySet<string>): string {
  const base = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'resource';
  let slug = base;
  for (let n = 2; taken.has(slug); n += 1) slug = `${base}-${n}`;
  return slug;
}

/** Fills in what the API derives: the reading time, the format line, what stops publishing. */
function derived(resource: SandboxResource): SandboxResource {
  const minutes = resource.format === RESOURCE_FORMAT.article ? Math.max(1, Math.round(articleText(resource.body).split(/\s+/).filter(Boolean).length / WORDS_PER_MINUTE)) : null;
  const formatLine =
    resource.format === RESOURCE_FORMAT.article
      ? `Article · ${minutes ?? 1} min read`
      : resource.format === RESOURCE_FORMAT.link
        ? resource.link
          ? `Link · ${resource.link.host}`
          : 'Link'
        : [resource.file?.kind ?? 'File', resource.file?.pageCount ? `${resource.file.pageCount} pages` : null].filter(Boolean).join(' · ');
  return { ...resource, readingMinutes: minutes, formatLine, publishProblems: publishProblems(resource) };
}

function resourceOr404(store: SandboxStore, id: string): SandboxResource {
  const found = store.portal.resources.find((resource) => resource.id === id);
  if (!found) throw new SandboxError(404, 'Resource not found.');
  return found;
}

function save(store: SandboxStore, resource: SandboxResource): SandboxResource {
  const next = derived({ ...resource, updatedAt: now() });
  store.portal.resources = store.portal.resources.some((row) => row.id === next.id) ? store.portal.resources.map((row) => (row.id === next.id ? next : row)) : [...store.portal.resources, next];
  return next;
}

/** Applies what the editor sent to a resource. */
function applyInput(store: SandboxStore, resource: SandboxResource, input: Partial<ResourceInput>): SandboxResource {
  const topic = input.topicId === undefined ? resource.topic : (store.portal.topics.find((candidate) => candidate.id === input.topicId) ?? null);
  const audience =
    input.audience === undefined
      ? resource.audience
      : input.audience.scope === RESOURCE_AUDIENCE.allClients
        ? { scope: RESOURCE_AUDIENCE.allClients, organizations: [] }
        : {
            scope: RESOURCE_AUDIENCE.chosenClients,
            organizations: input.audience.organizationIds.flatMap((id) => store.portal.organizations.filter((organization) => organization.id === id).map((organization) => ({ id: organization.id, name: organization.name }))),
          };
  const url = input.url?.trim();
  return {
    ...resource,
    ...(input.title === undefined ? {} : { title: input.title }),
    ...(input.summary === undefined ? {} : { summary: input.summary }),
    ...(input.format === undefined ? {} : { format: input.format }),
    ...(input.tags === undefined ? {} : { tags: input.tags }),
    ...(input.body === undefined ? {} : { body: input.body }),
    ...(url === undefined ? {} : { link: url && isHttpsUrl(url) ? { url, host: new URL(url).hostname.replace(/^www\./, '') } : null }),
    topic: topic ? { id: topic.id, name: topic.name } : null,
    audience,
  };
}

/** The list row: the resource without its article. */
function rowOf(resource: SandboxResource): Resource {
  const { body: _body, publishProblems: _problems, organizationViews: _views, lastOpenedAt: _opened, ...row } = resource;
  return row;
}

function topicsWithCounts(store: SandboxStore): ResourceTopic[] {
  return [...store.portal.topics]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((topic) => ({ ...topic, resourceCount: store.portal.resources.filter((resource) => resource.topic?.id === topic.id && resource.status !== RESOURCE_STATUS.archived).length }));
}

const TRANSITIONS: Readonly<Record<ResourceAction, (resource: SandboxResource) => SandboxResource>> = {
  [RESOURCE_ACTION.publish]: (resource) => {
    const problems = publishProblems(resource);
    if (problems.length > 0) throw new SandboxError(400, problems.join(' '));
    return { ...resource, status: RESOURCE_STATUS.published, publishedAt: resource.publishedAt ?? now() };
  },
  [RESOURCE_ACTION.unpublish]: (resource) => ({ ...resource, status: RESOURCE_STATUS.draft }),
  [RESOURCE_ACTION.archive]: (resource) => ({ ...resource, status: RESOURCE_STATUS.archived }),
  [RESOURCE_ACTION.restore]: (resource) => ({ ...resource, status: RESOURCE_STATUS.draft }),
};

export const resourceRoutes: readonly Route[] = [
  route('GET', '/resources', ({ store }) => ({ data: store.portal.resources.map(rowOf) })),
  route('POST', '/resources', ({ store, body }) => {
    const input = body as ResourceInput;
    const blank: SandboxResource = {
      id: newId(),
      title: '',
      slug: slugify(input.title, new Set(store.portal.resources.map((resource) => resource.slug))),
      summary: '',
      format: input.format,
      status: RESOURCE_STATUS.draft,
      topic: null,
      tags: [],
      audience: { scope: RESOURCE_AUDIENCE.allClients, organizations: [] },
      formatLine: '',
      readingMinutes: null,
      file: null,
      link: null,
      body: null,
      publishProblems: [],
      publishedAt: null,
      createdAt: now(),
      updatedAt: now(),
      stats: { views: 0, downloads: 0, linkOpens: 0 },
      organizationViews: 0,
      lastOpenedAt: null,
    };
    return { data: save(store, applyInput(store, blank, input)) };
  }),
  route('GET', '/resources/:id', ({ store, params }) => ({ data: resourceOr404(store, params['id'] ?? '') })),
  route('PATCH', '/resources/:id', ({ store, params, body }) => ({ data: save(store, applyInput(store, resourceOr404(store, params['id'] ?? ''), body as Partial<ResourceInput>)) })),
  ...Object.values(RESOURCE_ACTION).map((action) =>
    route('POST', `/resources/:id/${action}`, ({ store, params }) => ({ status: 200, data: save(store, TRANSITIONS[action](resourceOr404(store, params['id'] ?? ''))) })),
  ),
  route('POST', '/resources/:id/file', ({ store, params, body }) => {
    const file = body instanceof FormData ? body.get('file') : null;
    if (!(file instanceof File)) throw new SandboxError(400, 'Choose a file.');
    const kind = file.type === 'application/pdf' ? 'PDF' : (file.name.split('.').pop() ?? 'File').toUpperCase();
    return { status: 200, data: save(store, { ...resourceOr404(store, params['id'] ?? ''), file: { name: file.name, type: file.type, kind, size: file.size, pageCount: null } }) };
  }),

  route('GET', '/resource-topics', ({ store }) => ({ data: topicsWithCounts(store) })),
  route('POST', '/resource-topics', ({ store, body }) => {
    const input = body as { name: string; description: string; icon: ResourceTopicIcon };
    if (input.name.trim() === '') throw new SandboxError(400, 'Name the topic.');
    const topic: ResourceTopic = { id: newId(), name: input.name.trim(), slug: input.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'), description: input.description, icon: input.icon, sortOrder: store.portal.topics.length, archived: false, resourceCount: 0 };
    store.portal.topics = [...store.portal.topics, topic];
    return { data: topic };
  }),
  route('PATCH', '/resource-topics/:id', ({ store, params, body }) => {
    const existing = store.portal.topics.find((topic) => topic.id === params['id']);
    if (!existing) throw new SandboxError(404, 'Topic not found.');
    const updated: ResourceTopic = { ...existing, ...(body as Partial<ResourceTopic>) };
    store.portal.topics = store.portal.topics.map((topic) => (topic.id === updated.id ? updated : topic));
    // A renamed topic renames on its resources too.
    store.portal.resources = store.portal.resources.map((resource) => (resource.topic?.id === updated.id ? { ...resource, topic: { id: updated.id, name: updated.name } } : resource));
    return { data: updated };
  }),

  route('GET', '/organizations/:id/resources', ({ store, params }) => {
    const organizationId = params['id'] ?? '';
    const rows: OrganizationResource[] = store.portal.resources
      .filter((resource) => resource.status !== RESOURCE_STATUS.archived && resource.audience.organizations.some((organization) => organization.id === organizationId))
      .map((resource) => ({
        ...rowOf(resource),
        alsoSharedWith: resource.audience.organizations.filter((organization) => organization.id !== organizationId),
        organizationViews: resource.organizationViews,
        lastOpenedAt: resource.lastOpenedAt,
      }));
    return { data: rows };
  }),
  route('POST', '/organizations/:id/resources', ({ store, params, body }) => {
    const organization = store.portal.organizations.find((candidate) => candidate.id === params['id']);
    if (!organization) throw new SandboxError(404, 'Organization not found.');
    const ids = new Set((body as { resourceIds?: string[] }).resourceIds ?? []);
    const shared: string[] = [];
    const alreadyEveryClient: string[] = [];
    for (const resource of store.portal.resources.filter((candidate) => ids.has(candidate.id))) {
      if (resource.audience.scope === RESOURCE_AUDIENCE.allClients) {
        alreadyEveryClient.push(resource.id);
        continue;
      }
      if (resource.audience.organizations.some((candidate) => candidate.id === organization.id)) continue;
      save(store, { ...resource, audience: { ...resource.audience, organizations: [...resource.audience.organizations, { id: organization.id, name: organization.name }] } });
      shared.push(resource.id);
    }
    return { data: { shared, alreadyEveryClient } };
  }),
];
